// src/@core/components/auth/ProtectedRoute.js
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from 'src/hooks/useAuth'

// Map of frontend routes to their corresponding permission keys
const ROUTE_PERMISSION_MAP = {
  '/dashboard': 'Dashboard',
  '/service': 'My Services',
  '/my-qms': 'Quotes Management',
  '/store-front': 'Storefront',
  '/team-management': 'Team Management',
  '/order': 'Orders',
  '/payments': 'Payments',
  '/subscriptions': 'Subscriptions',
  '/oujat-protection': 'Oujat Protection',
  '/dispute-center': 'Dispute Center',
  '/notification': 'Notifications',
  '/profile': 'Profile',
  '/chat': 'Main Chat'
}

const PUBLIC_AUTHENTICATED_ROUTES = ['/401', '/404', '/profile']

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth()
  //console.log('user: ', user);
  const location = useLocation()

  if (!user) {
    return <Navigate to='/login' replace />
  }

  const currentPath = location.pathname

  // Extract permissions array from user object
  // Try user.permission, fallback to user.roleId?.permissions if needed
  const permissions = user?.roleId?.permissions || []
  //console.log('permissions: ', permissions);

  // If permissions array is empty, allow access to all routes
  if (!permissions || permissions?.length === 0) {
    return children
  }

  const hasRouteAccess = checkRouteAccess(permissions, currentPath)

  if (!hasRouteAccess) {
    return <Navigate to='/401' replace />
  }

  return children
}

/**
 * Check if user permissions array has access to the given route
 * @param {array} permissions - Array of permission strings
 * @param {string} path - Current route path
 * @returns {boolean} - Whether user has access
 */
const checkRouteAccess = (permissions, path) => {
  // Allow access to public routes
  if (PUBLIC_AUTHENTICATED_ROUTES.some(route => path === route || path.startsWith(route + '/'))) {
    return true
  }

  // Find the permission key for the current route
  const permissionKey = Object.entries(ROUTE_PERMISSION_MAP).find(
    ([route]) => path === route || path.startsWith(route + '/')
  )?.[1]

  // If no permission mapping exists, deny access
  if (!permissionKey) {
    return false
  }

  // Special handling for Payments & Subscriptions permission
  // If user has "Payments & Subscriptions" permission, they can access both Payments and Oujat Protection routes
  if (permissions.includes('Payments & Subscriptions')) {
    if (path === '/payments' || path.startsWith('/payments/')) {
      return true
    }
    if (path === '/subscriptions' || path.startsWith('/subscriptions/')) {
      return true
    }
    if (path === '/oujat-protection' || path.startsWith('/oujat-protection/')) {
      return true
    }
  }

  // Check if user has this permission
  return permissions.includes(permissionKey)
}

/**
 * Get accessible routes for a given permissions array
 * @param {array} permissions - Array of permission strings
 * @returns {array} - Array of accessible routes
 */
export const getAccessibleRoutes = permissions => {
  if (!permissions) return [...PUBLIC_AUTHENTICATED_ROUTES]

  // If no permissions are assigned, return all possible routes
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return [
      ...PUBLIC_AUTHENTICATED_ROUTES,
      '/dashboard',
      '/service',
      '/my-qms',
      '/store-front',
      '/team-management',
      '/order',
      '/payments',
      '/subscriptions',
      '/profile',
      '/oujat-protection',
      '/dispute-center',
      '/notification',
      '/chat'
      // "/open-quote", // Uncomment if needed
      // Add other possible routes here
    ]
  }

  // Filter routes based on permissions
  const accessibleRoutes = Object.entries(ROUTE_PERMISSION_MAP)
    .filter(([_, permissionKey]) => permissions.includes(permissionKey))
    .map(([route]) => route)

  // Special handling: If user has "Payments & Subscriptions" permission, add both payments routes
  if (permissions.includes('Payments & Subscriptions')) {
    if (!accessibleRoutes.includes('/payments')) {
      accessibleRoutes.push('/payments')
    }
    if (!accessibleRoutes.includes('/subscriptions')) {
      accessibleRoutes.push('/subscriptions')
    }
    if (!accessibleRoutes.includes('/oujat-protection')) {
      accessibleRoutes.push('/oujat-protection')
    }
  }

  return [...PUBLIC_AUTHENTICATED_ROUTES, ...accessibleRoutes]
}

export default ProtectedRoute
